/**
 * API client de Link Publico de Orcamento (Bloco G — modo apresentacao).
 *
 *   POST /api/orcamentos/{id}/gerar-link-publico    body: {dias_validade?}
 *   GET  /api/orcamento-links-publicos?orcamento={id}
 *   DELETE /api/orcamento-links-publicos/{id}       (revoga)
 *
 * Rotas publicas (sem auth — chamadas direto via fetch, NAO usar `request`):
 *   GET  /publico/orcamento/{token}
 *   POST /publico/orcamento/{token}/aprovar
 *   POST /publico/orcamento/{token}/recusar          body: {motivo}
 */

import { ACCESS_API_URL as API_BASE } from "../config/api";

export function gerarLinkPublico(request, orcamentoId, { diasValidade } = {}) {
  const body = {};
  if (diasValidade) body.dias_validade = diasValidade;
  return request(`/api/orcamentos/${orcamentoId}/gerar-link-publico`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listarLinks(request, orcamentoId) {
  return request(`/api/orcamento-links-publicos?orcamento=${orcamentoId}`);
}

export function revogarLink(request, id) {
  return request(`/api/orcamento-links-publicos/${id}`, { method: "DELETE" });
}

/**
 * Security Sprint 2 (A11/C2): o `token` NAO eh exposto na listagem de links.
 * Para obter a URL completa de um link existente, chama este endpoint dedicado
 * (auditado: evento `orcamento_link_copiado` com ator + IP + UA).
 * Retorna { token, url, expira_em }.
 */
export function copiarLinkPublico(request, linkId) {
  return request(`/api/orcamentos/copiar-link-publico`, {
    method: "POST",
    body: JSON.stringify({ link_id: linkId }),
  });
}

/**
 * Monta a URL publica que o cliente vai abrir. Usa `window.location.origin`
 * pra coincidir com onde o frontend esta hospedado.
 */
export function urlPublicaDoLink(token) {
  if (typeof window === "undefined") return `/p/orcamento/${token}`;
  return `${window.location.origin}/p/orcamento/${token}`;
}

// ===== Publico (sem auth) — fetch direto =====

// Para a pagina publica /p/orcamento/:token (sem auth) nao temos acesso ao
// `request` do AuthContext, entao fazemos fetch direto com a URL base da API
// (fonte unica em src/config/api.js).

async function _publicFetch(path, init = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: { "Content-Type": "application/json", ...(init.headers || {}) },
  });
  let body = null;
  try { body = await res.json(); } catch (_) { /* sem body */ }
  if (!res.ok) {
    const erro = new Error(body?.detalhe || `HTTP ${res.status}`);
    erro.status = res.status;
    erro.body = body;
    throw erro;
  }
  return body;
}

export function visualizarOrcamentoPublico(token) {
  return _publicFetch(`/publico/orcamento/${token}`);
}

export function aprovarOrcamentoPublico(token) {
  return _publicFetch(`/publico/orcamento/${token}/aprovar`, { method: "POST" });
}

/**
 * @deprecated F.5: usar `solicitarRevisaoOrcamentoPublico` (cliente nao
 * recusa final; pede revisao com comentario, orcamento volta a
 * EM_NEGOCIACAO e vendedor recebe email).
 *
 * Mantida temporariamente para retrocompat — backend trata ambas as
 * URLs (recusar e revisar) com o mesmo handler.
 */
export function recusarOrcamentoPublico(token, comentario) {
  return _publicFetch(`/publico/orcamento/${token}/revisar`, {
    method: "POST",
    body: JSON.stringify({ comentario }),
  });
}

/**
 * F.5: cliente solicita revisao do orcamento via link publico.
 *
 * Comentario obrigatorio. Backend valida e devolve 400 se vazio.
 * Orcamento muda para EM_NEGOCIACAO (se nao estava ja la) e vendedor
 * recebe email com o comentario.
 */
export function solicitarRevisaoOrcamentoPublico(token, comentario) {
  return _publicFetch(`/publico/orcamento/${token}/revisar`, {
    method: "POST",
    body: JSON.stringify({ comentario }),
  });
}
